import React, { ReactNode, useState } from 'react';
import {
  AppointmentConfirmationList,
  ProductSlotsPage,
  AppointmentConclusion,
  ServiceType,
  IndexPage,
  UnitList,
  SpecialistList,
  SpecialtyList,
  PendingSchedulePage
} from '../pages';

import { CareplusContext, ICareplusContext, Page } from './context';
import { useContextSelector } from 'use-context-selector';
import Header from '../../../components/Header';
import styled from 'styled-components';
import { ModalConfirmClose } from '../../../components/ModalConfirmClose';
import { PlatformContext } from '../../../contexts/PlatformContext';
import useOnCloseSchedule from '../../../hooks/useOnCloseSchedule';

const Template = styled.div`
  padding-top: 72px;
  height: 100%;
`;

const Pages: Record<Page, { title: string; nextPage: string; component: (props: any) => JSX.Element }> = {
  [Page.INDEX]: { title: '', nextPage: '', component: IndexPage },
  [Page.PRODUCT_SLOTS]: { title: 'Data e horário', nextPage: 'Resumo', component: ProductSlotsPage },
  [Page.APPOINTMENT_CONFIRMATION]: {
    title: 'Confirmação',
    nextPage: 'Conclusão',
    component: AppointmentConfirmationList
  },
  [Page.CONCLUSION]: { title: 'Conclusão', nextPage: '', component: AppointmentConclusion },
  [Page.APPOINTMENT_TYPE]: { title: 'Tipo de atendimento', nextPage: 'Data e horário', component: ServiceType },
  [Page.UNITS]: { title: 'Unidades', nextPage: 'Seleção do especialista', component: UnitList },
  [Page.SPECIALIST_LIST]: { title: 'Especialistas', nextPage: 'Tipo de atendimento', component: SpecialistList },
  [Page.SPECIALTY_LIST]: { title: 'Especialidades', nextPage: 'Unidades', component: SpecialtyList },
  [Page.PENDING_SCHEDULE_PAGE]: { title: 'Unidades', nextPage: '', component: PendingSchedulePage }
};

type RouterHistory = {
  index: number;
  page: Page;
  component: ReactNode;
  params: Record<string, unknown>;

  title: string;
  nextPage: string;
};

export function CaplusRouter(routeParams: any) {
  const [historyList, setHistoryList] = useState<RouterHistory[]>([]);
  const [lastIndex, setLastIndex] = useState(0);

  const { isModalCloseOpen, setModalCloseOpen } = useContextSelector(PlatformContext, state => state);

  const addHistoryList = ({ page, params, component }: Omit<RouterHistory, 'index' | 'title' | 'nextPage'>) => {
    setHistoryList(list => [
      ...list,
      {
        page,
        params,
        component,
        index: lastIndex + 1,
        title: Pages[page].title,
        nextPage: Pages[page].nextPage
      }
    ]);
  };

  const addHistory = ({ page, params }: Omit<RouterHistory, 'index' | 'component' | 'title' | 'nextPage'>) => {
    const PageComponent = Pages[page].component;
    const index = lastIndex + 1;
    const component = <PageComponent key={`${page}-${index}`} {...params} />;
    addHistoryList({ page, component, params });
    setLastIndex(index);
  };

  React.useEffect(() => {
    if (historyList.length === 0) addHistory({ page: Page.INDEX, params: routeParams });
  }, []);

  const navigate = (page: Page, params = {} as Record<string, unknown>) => {
    addHistory({ page, params });
  };

  const goBack = () => {
    setHistoryList(historyList.slice(0, -1));
    setLastIndex(lastIndex - 1);
  };

  const value: ICareplusContext = {
    navigate,
    goBack,
    history: historyList,
    setHistory: setHistoryList
  };

  const currentPage = historyList.find(({ index, component }) => {
    if (index === lastIndex) return component;
  });

  const { onCloseSchedule, confirmReset } = useOnCloseSchedule({});

  return (
    <CareplusContext.Provider value={value}>
      <Template>
        <Header onClose={onCloseSchedule} title={currentPage?.title} nextPage={currentPage?.nextPage} />
        {currentPage?.component}
      </Template>
      <ModalConfirmClose
        visible={!!isModalCloseOpen}
        onConfirmClose={confirmReset}
        onCancelClose={() => setModalCloseOpen?.(false)}
      />
    </CareplusContext.Provider>
  );
}
